import type { PublicSoundtrackItem, PublicSoundtrackState } from './types';

export type SoundtrackMediaType =
  | 'youtube'
  | 'spotify'
  | 'apple_music'
  | 'soundcloud'
  | 'audio'
  | 'external';

export const SOUNDTRACK_SOURCE_BADGE: Record<
  SoundtrackMediaType,
  { label: string; color: string }
> = {
  youtube: { label: 'YouTube', color: '#dc2626' },
  spotify: { label: 'Spotify', color: '#1db954' },
  apple_music: { label: 'Apple Music', color: '#fb2c5a' },
  soundcloud: { label: 'SoundCloud', color: '#ff5500' },
  audio: { label: 'Original Audio', color: '#155f52' },
  external: { label: 'External', color: '#64748b' },
};

const PUBLIC_SOUNDTRACK_VISIBILITIES = new Set([
  'public_profile',
  'public',
  'Profile Visible',
  'Public Profile',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isDirectAudioUrl(url: string): boolean {
  return /\.(mp3|wav|ogg|m4a|aac|flac)(\?.*)?$/i.test(url);
}

function buildSpotifyEmbed(url: string): string | null {
  const match = url.match(
    /open\.spotify\.com\/(track|album|playlist|episode|show)\/([A-Za-z0-9]+)/
  );
  return match
    ? `https://open.spotify.com/embed/${match[1]}/${match[2]}?utm_source=generator&theme=0`
    : null;
}

function buildAppleMusicEmbed(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes('music.apple.com')) return null;
    const parts = parsed.pathname.split('/').filter(Boolean);
    if (parts.length < 3 || parts[1] === 'artist') return null;
    return `https://embed.music.apple.com${parsed.pathname}${parsed.search}`;
  } catch {
    return null;
  }
}

function buildSoundCloudEmbed(url: string): string | null {
  if (!/soundcloud\.com\//i.test(url)) return null;
  return (
    'https://w.soundcloud.com/player/?url=' +
    encodeURIComponent(url) +
    '&color=%23ff5500&auto_play=false&hide_related=false&show_comments=false&show_user=true&show_reposts=false&show_teaser=false'
  );
}

function buildYouTubeEmbed(url: string): string | null {
  try {
    const parsed = new URL(url);
    let id = '';
    if (parsed.hostname.includes('youtu.be')) {
      id = parsed.pathname.split('/').filter(Boolean)[0] || '';
    } else if (parsed.hostname.includes('youtube.com')) {
      id = parsed.searchParams.get('v') || '';
      if (!id && parsed.pathname.includes('/embed/')) {
        id = parsed.pathname.split('/embed/')[1]?.split('/')[0] || '';
      }
    }
    if (!id || !/^[A-Za-z0-9_-]{11}$/.test(id)) return null;
    return `https://www.youtube-nocookie.com/embed/${id}?autoplay=0&rel=0&modestbranding=1`;
  } catch {
    return null;
  }
}

export function getSoundtrackUrl(item: PublicSoundtrackItem): string {
  const candidate =
    item.url ||
    item.source_url ||
    item.spotify_url ||
    item.youtube_url ||
    item.external_url ||
    item.audio_url ||
    '';
  return typeof candidate === 'string' ? candidate.trim() : '';
}

export function detectSoundtrackType(item: PublicSoundtrackItem): SoundtrackMediaType {
  const url = getSoundtrackUrl(item);
  if (isDirectAudioUrl(url)) return 'audio';
  if (/(?:youtube\.com\/watch|youtu\.be\/|music\.youtube\.com\/watch)/i.test(url)) {
    return 'youtube';
  }
  if (/open\.spotify\.com\/(track|album|playlist|episode|show)/i.test(url)) {
    return 'spotify';
  }
  if (/music\.apple\.com\//i.test(url)) return 'apple_music';
  if (/soundcloud\.com\//i.test(url)) return 'soundcloud';

  const declared = (item.source_type || item.source || '').toString();
  if (declared === 'uploaded_audio') return 'audio';
  if (
    declared === 'youtube' ||
    declared === 'spotify' ||
    declared === 'apple_music' ||
    declared === 'soundcloud'
  ) {
    return declared;
  }
  return 'external';
}

export function resolveSoundtrack(item: PublicSoundtrackItem): {
  type: SoundtrackMediaType;
  embedUrl: string | null;
  url: string;
} {
  const url = getSoundtrackUrl(item);
  const type = detectSoundtrackType(item);
  let embedUrl: string | null = null;
  if (type === 'youtube') embedUrl = buildYouTubeEmbed(url);
  else if (type === 'spotify') embedUrl = buildSpotifyEmbed(url);
  else if (type === 'apple_music') embedUrl = buildAppleMusicEmbed(url);
  else if (type === 'soundcloud') embedUrl = buildSoundCloudEmbed(url);
  return { type, embedUrl, url };
}

export function isSoundtrackInlineExpandable(type: SoundtrackMediaType): boolean {
  return (
    type === 'audio' ||
    type === 'youtube' ||
    type === 'spotify' ||
    type === 'apple_music' ||
    type === 'soundcloud'
  );
}

export function canPlaySoundtrackInline(item: PublicSoundtrackItem): boolean {
  const { type, embedUrl, url } = resolveSoundtrack(item);
  return type === 'audio' ? Boolean(url) : isSoundtrackInlineExpandable(type) && Boolean(embedUrl);
}

export function opensSoundtrackExternally(type: SoundtrackMediaType): boolean {
  return type === 'external';
}

export function getFeaturedSoundtrack(items: PublicSoundtrackItem[]): PublicSoundtrackItem | null {
  return items.find((item) => item.isFeatured || item.is_featured) || items[0] || null;
}

/**
 * The public API's soundtrack projection. This runs before serialization so
 * private/circle-only items never reach public-profile client code.
 */
export function projectPublicSoundtrack({
  items,
  enabled,
  hasAccess,
  isOwner,
}: {
  items: unknown;
  enabled: boolean;
  hasAccess: boolean;
  isOwner: boolean;
}): { items: PublicSoundtrackItem[]; state: PublicSoundtrackState } {
  const allItems = Array.isArray(items)
    ? items.filter(isRecord).map((item) => item as PublicSoundtrackItem)
    : [];
  const publicItems =
    enabled && hasAccess
      ? allItems.filter(
          (item) =>
            PUBLIC_SOUNDTRACK_VISIBILITIES.has(String(item.visibility || '')) &&
            Boolean(getSoundtrackUrl(item))
        )
      : [];

  return {
    items: publicItems,
    state: publicItems.length > 0 ? 'public' : isOwner && allItems.length > 0 ? 'private' : 'empty',
  };
}
